import React, { FunctionComponent } from 'react'
import { connect } from 'react-redux'

import { selectCurrentUserBadges } from '../../../reducers'

// SUPERUSER is never self-serviceable — only superusers may view/assign it
// (see adminBadgesForm.tsx) — so it shouldn't be advertised to a user as a
// badge they could go get.
function getRequestableBadges(badges: string[] = []) {
  return badges.filter((badge) => badge !== 'SUPERUSER')
}

function formatMissingBadgesMessage(visibleAnyBadges: string[]) {
  if (visibleAnyBadges.length > 1) {
    return `You're missing one of the following badges: ${visibleAnyBadges.join(
      ', '
    )}`
  }

  return `You're missing the following badge: ${visibleAnyBadges[0]}`
}

function getMissingBadges(userBadges: string[], requiredBadges: string[] = []) {
  let missingBadges: string[] = []

  return requiredBadges.reduce((missingBadges, requiredBadge) => {
    if (!userBadges?.includes(requiredBadge)) {
      missingBadges.push(requiredBadge)
    }

    return missingBadges
  }, missingBadges)
}

function userHasPermission(
  userBadges: string[],
  requiredBadges?: string[],
  anyBadges?: string[]
) {
  // When anyBadges is provided, the user passes if they hold at least one of
  // them; otherwise fall back to the all-of requiredBadges check.
  if (anyBadges && anyBadges.length > 0) {
    return anyBadges.some((badge) => userBadges?.includes(badge))
  }

  return getMissingBadges(userBadges, requiredBadges).length === 0
}

export interface Props {
  children: any // TODO-TS
  currentUserBadges: string[]
  disableUI?: boolean
  requiredBadges?: string[]
  anyBadges?: string[]
}

export const BadgeGateView: FunctionComponent<Props> = ({
  children,
  currentUserBadges = [],
  disableUI = false,
  requiredBadges,
  anyBadges,
}) => {
  switch (true) {
    case userHasPermission(currentUserBadges, requiredBadges, anyBadges):
      return children
    case disableUI: {
      const visibleAnyBadges = getRequestableBadges(anyBadges)
      return (
        <div
          className="badge-gate--disabled"
          title={
            visibleAnyBadges.length > 0
              ? formatMissingBadgesMessage(visibleAnyBadges)
              : `You're missing the following badges: ${getMissingBadges(
                  currentUserBadges,
                  requiredBadges
                ).join(', ')}`
          }
        >
          {children}
        </div>
      )
    }
    default: {
      const visibleAnyBadges = getRequestableBadges(anyBadges)
      return (
        <div data-testid="unauthorized-view" style={{ padding: '4rem' }}>
          <h1>Unauthorized</h1>

          <p>
            You need{' '}
            {visibleAnyBadges.length > 1
              ? 'one of the following badges'
              : 'the following badges'}{' '}
            to view this page:
          </p>
          <ul>
            {(visibleAnyBadges.length > 0
              ? visibleAnyBadges
              : requiredBadges || []
            ).map((badge) => (
              <li key={badge}>{badge}</li>
            ))}
          </ul>

          <p>These are your current badges:</p>
          <ul>
            {currentUserBadges.map((badge) => (
              <li key={badge}>{badge}</li>
            ))}
          </ul>
        </div>
      )
    }
  }
}

const mapStateToProps = (state) => {
  return {
    currentUserBadges: selectCurrentUserBadges(state),
  }
}

export const BadgeGate = connect(mapStateToProps)(BadgeGateView)
